import { useState, useEffect } from "react";
import { Box, TextField } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import CourseList from "@nualang/nualang-ui-components/dist/Lists/Courses";
import ContentTypeFilter from "@nualang/nualang-ui-components/dist/Forms/ContentTypeFilter/ContentTypeFilter";
import CoursesNotFound from "@nualang/nualang-ui-components/dist/Misc/NotFound/Courses";
import LanguageSelector from "@nualang/nualang-ui-components/dist/Forms/LanguageSelector/LanguageSelector";
import HideOnScroll from "@nualang/nualang-ui-components/dist/Misc/HideOnScroll";
import SortBySelector from "@nualang/nualang-ui-components/dist/Forms/SortBySelector/SortBySelector";
import useDebounce from "@nualang/nualang-ui-components/dist/hooks/useDebounce";
import { useLocation } from "react-router";

const useStyles = makeStyles()((theme, { disablePadding }) => ({
  root: {
    height: "100%",
    padding: disablePadding ? 0 : theme.spacing(1, 2),
    [theme.breakpoints.up("sm")]: {
      padding: disablePadding ? 0 : theme.spacing(2, 3),
    },
  },
  box: {
    padding: theme.spacing(1, 2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2, 3),
    },
  },
  autocomplete: {
    flexGrow: 1,
    marginRight: theme.spacing(1),
  },
  actions: {
    marginBottom: theme.spacing(1),
  },
  divider: {
    marginBottom: theme.spacing(1),
  },
}));

function SearchFilters({
  t,
  classes,
  languageOptions,
  forLanguageOptions,
  handleFilterChange,
  sortByOptions,
  learnLang,
  search,
  setSearch,
  forLang,
  sort,
  difficulty,
  difficultyOptions,
  selectedTypes,
  typeCounts,
  handleTypesChange,
  query,
}) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const text = queryParams.get("searchText") || "";
  const [searchText] = useState(text);
  const debouncedSearch = useDebounce(searchText, 500);
  useEffect(() => {
    if (setSearch && searchText !== search) {
      setSearch(searchText);
      if (window.scrollY > 100) {
        window.scrollTo(0, 0);
      }
    }
  }, [debouncedSearch]);

  return (
    <HideOnScroll threshold={300}>
      <Box
        sx={[{
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 2, sm: 3 },
          pt: 1.5,
          pb: "12px",
        }, (theme) => ({
          position: "sticky",
          top: 0,
          zIndex: 1200,
          backgroundColor: theme.palette.background.default,
          borderBottom: `1px solid ${theme.palette.divider}`,
          // Small gap so the breadcrumbs' bottom shadow (Paper elevation) isn't
          // covered by this sticky bar's background.
          marginTop: theme.spacing(0.5),
        })]}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
            width: { xs: "100%", md: "60%" },
          }}
        >
          <LanguageSelector
            id={"learnLang"}
            name={"learnLang"}
            label={t("learn")}
            size="small"
            variant={"outlined"}
            sx={{ flex: "1 1 170px", minWidth: 150 }}
            t={t}
            languages={languageOptions}
            value={learnLang}
            onChange={handleFilterChange}
          />
          <LanguageSelector
            id={"forLang"}
            name={"forLang"}
            label={t("speakers_of")}
            size="small"
            variant={"outlined"}
            sx={{ flex: "1 1 170px", minWidth: 150 }}
            t={t}
            languages={forLanguageOptions}
            value={forLang}
            onChange={handleFilterChange}
          />
          <TextField
            select
            id='difficulty'
            name='difficulty'
            data-cy='difficulty'
            label={t("difficulty")}
            value={difficulty}
            onChange={handleFilterChange}
            size="small"
            variant={"outlined"}
            sx={{ flex: "1 1 150px", minWidth: 130 }}
          >
            {difficultyOptions}
          </TextField>
          <SortBySelector
            id={"sortBy"}
            name={"sort"}
            label={t("sort")}
            size="small"
            variant={"outlined"}
            sx={{ flex: "1 1 170px", minWidth: 150 }}
            t={t}
            value={sort}
            onChange={handleFilterChange}
            sortByOptions={sortByOptions}
            disabled={
              (!learnLang || learnLang === "all") &&
              (!forLang || forLang === "all")
            }
          />
        </Box>
        <ContentTypeFilter
          t={t}
          selected={selectedTypes}
          counts={typeCounts}
          onChange={handleTypesChange}
          query={query}
          showLabel={false}
          showSummary={false}
        />
      </Box>
    </HideOnScroll>
  );
}
export default function Search({
  t = (text) => text,
  courses = [],
  selectedCourses = [],
  isLoading,
  placeholderImageUrl,
  handleSelectAllCoursesClick,
  handleClickCourse,
  selectable,
  learnLang,
  forLang,
  handleViewCourseTopic,
  handleFilterChange,
  languages,
  forLanguages,
  handleStartCourse,
  handleLeaveCourse,
  handleViewCourse,
  handleViewUserProfile,
  handleCreateCourse,
  membersProps,
  getMemberDetails,
  memberPlaceholderImageUrl,
  disablePadding,
  getCourseSections,
  getCourseMember,
  getCourseMembers,
  memberId,
  username,
  sort,
  search,
  setSearch,
  handleDuplicateCourse,
  difficulty,
  difficultyOptions,
  selectedTypes = [],
  typeCounts = {},
  handleTypesChange,
  query = "",
  showTypeBadge = false,
  handleStartItem,
  handleViewParentCourse,
}) {
  const { classes } = useStyles({ disablePadding });
  const languageOptions = {
    all: {},
    ...languages,
  };
  const forLanguageOptions = {
    all: {},
    ...forLanguages,
  };

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const text = queryParams.get("searchText") || "";

  const sortByOptions =
    text === ""
      ? {
          newest: "newest",
          oldest: "oldest",
          popular: "popular",
        }
      : {
          newest: "newest",
          oldest: "oldest",
          popular: "popular",
          relevance: "relevance",
        };

  return (
    <div>
      <SearchFilters
        t={t}
        classes={classes}
        languageOptions={languageOptions}
        forLanguageOptions={forLanguageOptions}
        handleFilterChange={handleFilterChange}
        sortByOptions={sortByOptions}
        learnLang={learnLang}
        search={search}
        setSearch={setSearch}
        handleCreateCourse={handleCreateCourse}
        sort={sort}
        forLang={forLang}
        difficulty={difficulty}
        difficultyOptions={difficultyOptions}
        selectedTypes={selectedTypes}
        typeCounts={typeCounts}
        handleTypesChange={handleTypesChange}
        query={query}
      />
      {isLoading || (courses !== undefined && courses.length > 0) ? (
        <div className={classes.root}>
          <CourseList
            t={t}
            compact
            courses={
              courses !== undefined && Array.isArray(courses) && courses.length
                ? courses
                : null
            }
            handleStartCourse={handleStartCourse}
            handleStartItem={handleStartItem}
            handleViewParentCourse={handleViewParentCourse}
            showTypeBadge={showTypeBadge}
            handleLeaveCourse={handleLeaveCourse}
            handleViewCourse={handleViewCourse}
            handleViewUserProfile={handleViewUserProfile}
            membersProps={membersProps}
            handleViewCourseTopic={handleViewCourseTopic}
            selectable={selectable}
            selectedCourses={selectedCourses}
            handleSelectAllCoursesClick={handleSelectAllCoursesClick}
            handleClickCourse={handleClickCourse}
            getMemberDetails={getMemberDetails}
            memberPlaceholderImageUrl={memberPlaceholderImageUrl}
            getCourseSections={getCourseSections}
            getCourseMember={getCourseMember}
            getCourseMembers={getCourseMembers}
            memberId={memberId}
            username={username}
            handleDuplicateCourse={handleDuplicateCourse}
          />
        </div>
      ) : (
        <div className={classes.root}>
          <CoursesNotFound
            t={t}
            placeholderImageUrl={placeholderImageUrl}
            handleCreateCourse={handleCreateCourse}
            title={t("courses_not_found_title")}
            description={t("courses_not_found_desc")}
          />
        </div>
      )}
    </div>
  );
}
